/*
 * This file contains SDK documentation for stuff defined by the Logger package.
 * They are here to 1) ensure they are picked up by our documentation tool and
 *    2) ensure they are written from the perspective of the SDK (not the Logger
 *    library).
 */

/**
 * A LogEntry object is the data that the SDK compiles when information is
 *    logged. It contains both the logged information and meta-info about when
 *    and who logged it.
 *
 * A {@link logger.LogHandler LogHandler} provided to the SDK (see
 *    {@link #configconfiglogs config.logs}) will need to handle LogEntry
 *    objects.
 *
 * @public
 * @static
 * @typedef {Object} LogEntry
 * @memberof logger
 * @requires logs
 * @property {number} timestamp When the log was created, based on UNIX epoch.
 * @property {string} method The log function that was used to create the log.
 * @property {string} level The level of severity the log.
 * @property {Object} target The subject that the log is about.
 * @property {string} target.type The type of the target. This is also
 *    used as part of the name of the Logger.
 * @property {string} [target.id] A unique identifier for the target.
 * @property {string} target.name A combination of the target type and ID. If no
 *    id was provided, this will be the same as the type.
 * @property {Array} messages The logged information, given to the Logger
 *    method as parameters.
 * @property {Object} [timer] Timing data, if the log method was a timer method.
 * @example
 * function defaultLogHandler (logEntry) {
 *   // Compile the meta info of the log for a prefix.
 *   const { timestamp, level, target } = logEntry
 *   let { method } = logEntry
 *   const logInfo = `${timestamp} - ${target.type} - ${level}`
 *
 *   // Assume that the first message parameter is a string.
 *   const [log, ...extra] = logEntry.messages
 *
 *   // For the timer methods, don't actually use the console methods.
 *   //    The Logger already did the timing, so simply log out the info.
 *   if (['time', 'timeLog', 'timeEnd'].includes(method)) {
 *     method = 'debug'
 *   }
 *
 *   console[method](`${logInfo} - ${log}`, ...extra)
 * }
 */

/**
 * A LogHandler can be used to customize how the SDK should log information. By
 *    default, the SDK will log information to the console, but a LogHandler can
 *    be configured to change this behaviour.
 *
 * A LogHandler can be provided to the SDK as part of its configuration (see
 *    {@link #configconfiglogs config.logs}). The SDK will then provide this
 *    function with the logged information.
 *
 * @public
 * @static
 * @typedef {Function} LogHandler
 * @memberof logger
 * @requires logs
 * @param {Object} LogEntry The LogEntry to be logged.
 * @example
 * // Define a custom function to handle logs.
 * function logHandler (logEntry) {
 *   // Compile the meta info of the log for a prefix.
 *   const { timestamp, level, target } = logEntry
 *   let { method } = logEntry
 *   const logInfo = `${timestamp} - ${target.type} - ${level}`
 *
 *   // Assume that the first message parameter is a string.
 *   const [log, ...extra] = logEntry.messages
 *
 *   // For the timer methods, don't actually use the console methods.
 *   //    The Logger already did the timing, so simply log out the info.
 *   if (['time', 'timeLog', 'timeEnd'].includes(method)) {
 *     method = 'debug'
 *   }
 *
 *   console[method](`${logInfo} - ${log}`, ...extra)
 * }
 *
 * // Provide the LogHandler as part of the SDK configurations.
 * const configs = { ... }
 * configs.logs.handler = logHandler
 * const client = create(configs)
 */
